import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import { api } from "../App";
import { Check, Loader2, AlertCircle } from "lucide-react";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const method = searchParams.get("method");
  
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= 5) {
      setStatus("pending");
      return;
    }

    try {
      const response = await api.get(`/payments/stripe/status/${sessionId}`);
      setPaymentInfo(response.data);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch {
      setStatus("error");
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId && method !== "paypal") {
      pollPaymentStatus();
    } else if (method === "paypal") {
      setStatus("success");
    }
  }, [sessionId, method, pollPaymentStatus]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#FF5A1F] mx-auto mb-4" />
            <p className="text-slate-600">Verifying your payment...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-24 h-24 bg-[#16A34A]/10 mx-auto flex items-center justify-center mb-8">
                <Check className="w-12 h-12 text-[#16A34A]" />
              </div>
              
              <h1 className="text-4xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Payment Successful!
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                Thank you for your booking. Your deposit has been received and your move is now confirmed.
              </p>

              {paymentInfo && (
                <div className="bg-[#F8F9FA] p-6 mb-8 text-left">
                  <h3 className="font-semibold text-[#0F172A] mb-4">Payment Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Amount Paid</span>
                      <span className="font-medium">£{(paymentInfo.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status</span>
                      <span className="font-medium text-[#16A34A]">Paid</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-[#0F172A] p-6 text-white text-left mb-8">
                <h3 className="font-semibold mb-4">What Happens Next?</h3>
                <ul className="space-y-3 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <span>You'll receive a confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <span>We'll contact you 24-48 hours before your move</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <span>Remaining balance due on moving day</span>
                  </li>
                </ul>
              </div>

              <Link to="/" className="btn-primary" data-testid="success-home-btn">
                Return Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#EAB308]/10 mx-auto flex items-center justify-center mb-6">
                <AlertCircle className="w-10 h-10 text-[#EAB308]" />
              </div>
              <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Payment Processing
              </h1>
              <p className="text-slate-600 mb-8">
                Your payment is being processed. Please check your email for confirmation, 
                or contact us if you don't receive confirmation within 24 hours.
              </p>
              <Link to="/" className="btn-primary" data-testid="pending-home-btn">
                Return Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-red-100 mx-auto flex items-center justify-center mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
              Payment Issue
            </h1>
            <p className="text-slate-600 mb-8">
              There was an issue verifying your payment. Please contact us if you believe 
              your payment was successful.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="error-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
